/**
 * AccessoriesSection Component
 *
 * Displays suggested accessories for cross-selling on product detail pages.
 * Shows a horizontal scrollable list of accessory cards.
 */

import { useAccessoriesForEquipmentType } from '@/hooks/useAccessories';
import { useLocationStore } from '@/stores/useLocationStore';
import { AccessoryCard } from './AccessoryCard';
import { Loader2, Package2, Gift } from 'lucide-react';
import type { CustomerAccessoryResponse } from '@/types';

interface AccessoriesSectionProps {
    equipmentTypeId: string;
    title?: string;
    maxItems?: number;
    variant?: 'section' | 'panel';
    panelTitle?: string;
}

export const AccessoriesSection = ({
    equipmentTypeId,
    title = "Add Accessories",
    maxItems = 6,
    variant = 'section',
    panelTitle = 'Accessories',
}: AccessoriesSectionProps) => {
    const { location } = useLocationStore();

    const { data: accessories, isLoading, error } = useAccessoriesForEquipmentType(
        equipmentTypeId,
        location?.id,
        maxItems
    );

    // Handle add to cart (for analytics/tracking in future)
    const handleAddToCart = (_accessory: CustomerAccessoryResponse) => {
        // Future: track analytics event
    };

    // Don't show section if loading or no accessories
    if (isLoading) {
        return (
            <div className="mt-10 border-t border-border pt-10">
                <h3 className="text-lg font-medium text-foreground mb-4">{title}</h3>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
                </div>
            </div>
        );
    }

    if (error || !accessories || accessories.length === 0) {
        return null; // Don't show section if no accessories available
    }

    // Filter to only show available accessories
    const availableAccessories = accessories.filter(a => a.isAvailable);

    // Separate bundled (included) from standalone accessories
    const bundledAccessories = availableAccessories.filter(a => a.isBundled);
    const standaloneAccessories = availableAccessories.filter(a => !a.isBundled);

    if (availableAccessories.length === 0) {
        return null;
    }

    const content = (
        <>
            {/* Bundled/Included accessories section */}
            {bundledAccessories.length > 0 && (
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Gift className="h-5 w-5 text-green-600" />
                        <h4 className="text-base font-medium text-foreground">Included Accessories</h4>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            No extra charge
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        These accessories come with your rental at no additional cost
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {bundledAccessories.map((accessory) => (
                            <AccessoryCard
                                key={accessory.id}
                                accessory={accessory}
                                onAddToCart={handleAddToCart}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Standalone/Add-on accessories section */}
            {standaloneAccessories.length > 0 && (
                <>
                    <div className="flex items-center gap-2 mb-4">
                        <Package2 className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-medium text-foreground">{title}</h3>
                        <span className="text-sm text-muted-foreground">
                            ({standaloneAccessories.length} available)
                        </span>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4">
                        Enhance your rental with these recommended accessories
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {standaloneAccessories.map((accessory) => (
                            <AccessoryCard
                                key={accessory.id}
                                accessory={accessory}
                                onAddToCart={handleAddToCart}
                            />
                        ))}
                    </div>
                </>
            )}

            {accessories.length > availableAccessories.length && (
                <p className="text-xs text-muted-foreground/70 mt-3">
                    {accessories.length - availableAccessories.length} accessory(ies) currently unavailable at this location
                </p>
            )}
        </>
    );

    if (variant === 'panel') {
        return (
            <details open className="mt-4 border border-border rounded-lg bg-white">
                <summary className="cursor-pointer select-none px-4 py-3 text-xl font-semibold text-foreground">
                    {panelTitle}
                </summary>
                <div className="px-4 pb-4">
                    {content}
                </div>
            </details>
        );
    }

    return (
        <section className="mt-10 border-t border-border pt-10">
            {content}
        </section>
    );
};

export default AccessoriesSection;
